import React, { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import {
  KeyedItemOption,
  MultiSelectCheckboxFilterBase,
  MultiSelectCheckboxFilterBaseOption,
  MultiSelectCheckboxFilterBaseProps,
} from '../MultiSelectCheckboxFilterBase';
import {
  ListItem,
  useBuildOption,
  useDropdownSelectFilterState,
} from '../../hooks';

export interface MultiSelectCheckboxFilterProps<V>
  extends Omit<
    MultiSelectCheckboxFilterBaseProps<V>,
    'options' | 'dropdownSelect'
  > {
  data: V[];
  /**
   * A function that returns a [ListItemSelectProps](https://www.docs.wixdesignsystem.com/?path=/story/components-form-dropdownlistitems--listitemselect) (Optional)
   */
  renderItem?: (item: V, index: number) => ListItem;
}

function _MultiSelectCheckboxFilter<V>(
  props: MultiSelectCheckboxFilterProps<V>,
) {
  const { renderItem, filter, data, onSelect, onDeselect, ...rest } = props;

  const keyedItems = useMemo(
    () =>
      data.map((item, index) => {
        const key = filter.itemKey(item);
        return {
          id: key,
          key,
          item,
          index,
          indexWithinPage: index,
          pageIndex: 0,
        };
      }),
    [data],
  );

  const buildOption = useBuildOption({
    filter,
    renderItem,
    checkbox: true,
  });

  const { dropdownSelect, init } = useDropdownSelectFilterState<V>({
    filter,
  });

  useEffect(() => {
    return init();
  }, []);

  const options: MultiSelectCheckboxFilterBaseOption<V>[] =
    keyedItems.map(buildOption);

  return (
    <MultiSelectCheckboxFilterBase
      {...rest}
      options={options}
      filter={filter}
      dropdownSelect={dropdownSelect}
      onClose={() => {
        dropdownSelect.close();
      }}
      onSelect={(id, option) => {
        const { keyedItem } = option as KeyedItemOption<V>;
        dropdownSelect.set(keyedItem);
        onSelect?.(id, option);
      }}
      onDeselect={(id, option) => {
        const { keyedItem } = option as KeyedItemOption<V>;
        dropdownSelect.delete(keyedItem);
        onDeselect?.(id, option);
      }}
    />
  );
}

export const MultiSelectCheckboxFilter = observer(_MultiSelectCheckboxFilter);
